// Shared cache-first core for the two Cache-Storage layers in this app:
//   - src/lib/aamAssetCache.js    (AAM /ogs decoders + hair.gsh; size-versioned key)
//   - src/lib/staticAssetCache.js (bundled ORT wasm; URL key)
// Both open a cache, match a key, serve a hit with no network, otherwise fetch and
// (on an ok response) store a SINGLE clone while driving the boot progress bar off
// the cache write completing. Each layer keeps only its own key derivation +
// request interception; everything below is identical between them.

import { assetLoadProgress } from './assetLoadProgress.js'

// `cache`     — an already-opened Cache (each caller owns open()/passthrough).
// `key`       — cache key to match/put under (may differ from the progress id).
// `fetchImpl` — zero-arg thunk performing the underlying network fetch.
// opts:
//   id     — progress-bar task id (aam uses the path, static uses the url).
//   label  — friendly progress label.
//   sizeOf — (res) => byte total for the progress bar; called for both the hit and
//            the fetched response (aam ignores res and returns the known size;
//            static reads content-length off the response). Defaults to 0.
//   meter  — drive assetLoadProgress (markCached/register/complete/fail). Default true.
//   requireStatus200 — also gate caching on res.status === 200 (static only; a 206
//            partial must never be cache-served). Default false → gate on res.ok alone.
// Never caches a non-ok response. Returns the response (hit or fetched) unchanged.
export async function cacheFirst(cache, key, fetchImpl, opts = {}) {
  const {
    id, label, sizeOf = () => 0, meter = true, requireStatus200 = false,
    // Declared byte count (0 = unknown, skip the check), as a MINIMUM. A body
    // shorter than declared is a truncated download: never cached, or the broken
    // bytes are replayed on every subsequent load and a reload "changes nothing".
    //
    // A body LONGER than declared is a different thing entirely and must not be
    // treated as broken: a truncation cannot add bytes, so what it means is that
    // the declared size is wrong. Field case: the AAM's /ogs rows for a bundle
    // carried the BROTLI-COMPRESSED transfer length (export_manifest.json: 940
    // declared, 3132 actual — exactly the `Content-Encoding: br` Content-Length),
    // so every file of that character "failed" verification and NOTHING cached —
    // a ~500 MB re-download on every single load, reported as 15 console.errors.
    // Refusing the cache is the expensive half of that; the check exists to catch
    // short bodies, so only short bodies fail it. `onOversize` reports the bad
    // metadata and the body is cached (the declared size still versions the key).
    verifyBytes = 0, onShort = null, onOversize = null,
  } = opts

  const hit = await cache.match(key)
  if (hit) {
    // Trust, but verify: entries written BEFORE the short-body check existed
    // (or by a browser that stored a chunked stream that ended early) are
    // poisoned copies that would otherwise be replayed forever — the store is
    // cache-first, so no reload and no deploy can ever dislodge them. Evict
    // and fall through to a fresh download instead of serving them.
    if (verifyBytes > 0) {
      const buf = await hit.arrayBuffer()
      if (buf.byteLength >= verifyBytes) {
        if (meter) assetLoadProgress.markCached(id, label, sizeOf(hit))
        return new Response(buf, { status: hit.status, statusText: hit.statusText, headers: hit.headers })
      }
      console.warn(
        `[cache-first] evicting poisoned cache entry for ${id}: ` +
        `${buf.byteLength} of ${verifyBytes} bytes (short) — refetching`)
      try { await cache.delete(key) } catch { /* eviction is best-effort */ }
      // fall through to the network fetch below
    } else {
      if (meter) assetLoadProgress.markCached(id, label, sizeOf(hit)) // already local, no download
      return hit
    }
  }

  const res = await fetchImpl()
  if (!(res && res.ok && (!requireStatus200 || res.status === 200))) return res

  if (meter) assetLoadProgress.register(id, label, sizeOf(res))

  // Read the body ONCE, fully, and hand the caller its own copy.
  //
  // This used to be `cache.put(key, res.clone())` with the caller reading the
  // other branch of the tee — which couples them: a cache write that fails
  // partway (quota, and an /ogs bundle is ~500 MB against a per-origin limit)
  // disturbs the shared source, and the CALLER's read can then end early with
  // no error at all. That is how eyeline's 18 MB mesh_head.bin kept arriving as
  // exactly 14548992 bytes — deterministic, surviving a cache clear (the quota
  // refills and fails at the same place), and fine on localhost where the quota
  // is far larger. A cache write must never be able to truncate the download.
  //
  // Buffering costs nothing extra: every caller already does
  // `new Uint8Array(await res.arrayBuffer())`, so the bytes are fully
  // materialised regardless.
  const buf = await res.arrayBuffer()
  const copyOf = () => new Response(buf, {
    status: res.status,
    statusText: res.statusText,
    headers: res.headers,
  })

  // A short body is a broken download, not a cacheable asset. Caching it would
  // make every later load replay the same bad bytes — which is exactly why
  // clearing the cache appeared to change nothing.
  if (verifyBytes > 0 && buf.byteLength < verifyBytes) {
    onShort?.(buf.byteLength, verifyBytes)
    if (meter) assetLoadProgress.complete(id)
    return copyOf()
  }
  // More bytes than declared: the download is fine, the METADATA is wrong (see the
  // verifyBytes note above). Report it and cache anyway — the alternative is
  // re-downloading the whole bundle on every load because a server row is stale.
  if (verifyBytes > 0 && buf.byteLength > verifyBytes) onOversize?.(buf.byteLength, verifyBytes)

  // Best-effort cache write, now fully detached from what the caller gets.
  const put = cache.put(key, copyOf())
  if (meter) put.then(() => assetLoadProgress.complete(id), () => assetLoadProgress.complete(id))
  else put.catch(() => { /* opaque/partial/quota — non-fatal */ })

  return copyOf()
}
